import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  /** Stable id of the modal instance; a change resets the boundary. */
  resetKey: string;
  /** Called when a modal throws, so the host can pop the broken instance. */
  onError: (error: Error) => void;
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Isolates a single modal. A throw inside one modal is contained here — it
 * calls `onError` (the host closes that instance) and renders nothing, instead
 * of bubbling to the app-level boundary and blanking the whole screen.
 */
export class ModalErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      '[ModalHost] Modal crashed and was closed:',
      error,
      info.componentStack,
    );
    this.props.onError(error instanceof Error ? error : new Error(String(error)));
  }

  override componentDidUpdate(prev: Props): void {
    // Reset when a different instance reuses this boundary slot.
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  override render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
